import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { BOAPI } from '../service/UsersService';
import utils from '../utils/utils';
import { commissionLabel, discountLabel } from '../utils/benefit';
import BusinessEditModal from './BusinessEditModal';
import FollowUpModal from './FollowUpModal';
import ImportBusinessesModal from './ImportBusinessesModal';
import ImportPendingBusinessesModal from './ImportPendingBusinessesModal';

// תמונה יכולה להיות קישור חיצוני מלא (http/https) ולא רק נתיב /uploads/...
// מקומי - ראו הערה זהה ב-BusinessEditModal.jsx
const fullUrl = (path) => (!path ? null : /^https?:\/\//i.test(path) ? path : `${utils.BaseUrl}${path}`);

const fmtFollowUpDate = (d) => new Date(d).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
// "היום" מחושב בזמן מקומי (לא UTC) - אותה סיבה בדיוק כמו localDateStr ב-AnnouncementsTab.jsx
const isFollowUpDue = (d) => {
  const dt = new Date(d);
  const today = new Date();
  dt.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return dt <= today;
};

// ברירת המחדל של הסינון/מיון בטאב - נשמרת בדפדפן של איש הצוות ("⭐ שמירה כברירת
// מחדל"), כך שלכל אחד יכולה להיות ברירת מחדל משלו. לא קריטי - בלי localStorage
// (מצב פרטי וכו') פשוט מתחילים מ"הכל"
const FILTERS_STORAGE_KEY = 'bo.businesses.defaultFilters';
const ALL_FILTERS = { status: 'all', owner: 'all', store: 'all', sortKey: 'order', sortDir: 'asc' };
const loadDefaultFilters = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(FILTERS_STORAGE_KEY) || 'null');
    return saved && typeof saved === 'object' ? { ...ALL_FILTERS, ...saved } : ALL_FILTERS;
  } catch {
    return ALL_FILTERS;
  }
};

const STATUS_TABS = [
  { key: 'all', label: 'הכל' },
  { key: 'active', label: 'פעיל' },
  { key: 'inactive', label: 'מוסתר' },
];

// סטטוס תהליך גיוס - 'confirmed' נגזר תמיד מ-ownerConfirmedAt (לא מהעמודה
// הגולמית leadStatus - ראו effectiveLeadStatus), שאר השלושה נקבעים ידנית
// מ-BusinessEditModal.jsx כל עוד העסק עוד לא אושר בפועל
const LEAD_STATUS_INFO = {
  confirmed:   { label: 'אושר', style: 'statusActive' },
  pending:     { label: 'ממתין', style: 'statusPending' },
  in_progress: { label: 'בתהליך', style: 'statusInProgress' },
  rejected:    { label: 'נדחה', style: 'statusInactive' },
};
const effectiveLeadStatus = (b) => (b.ownerConfirmedAt ? 'confirmed' : (b.leadStatus || 'pending'));

const OWNER_CONFIRM_TABS = [
  { key: 'all', label: 'הכל' },
  { key: 'confirmed', label: 'אושר' },
  { key: 'pending', label: 'ממתין' },
  { key: 'in_progress', label: 'בתהליך' },
  { key: 'rejected', label: 'נדחה' },
];

// חנות מקוונת: physical = חנות פיזית בלבד; online/both = יש אתר, ומחובר/ממתין
// לפי onlineConnected (חיבור WooCommerce/Shopify הושלם)
const storeState = (b) => (!b.businessType || b.businessType === 'physical' ? 'physical' : b.onlineConnected ? 'connected' : 'waiting');
const STORE_TABS = [
  { key: 'all', label: 'הכל' },
  { key: 'waiting', label: 'ממתין לחיבור' },
  { key: 'connected', label: 'מחובר' },
  { key: 'physical', label: 'חנות פיזית' },
];
const STORE_INFO = {
  connected: { label: 'מחובר', style: 'statusActive' },
  waiting:   { label: 'ממתין לחיבור', style: 'statusPending' },
  physical:  { label: 'חנות פיזית', style: 'statusNeutral' },
};

// מיון בלחיצה על כותרת עמודה - לחיצה שנייה הופכת כיוון
const SORTERS = {
  name:     (b) => b.name || '',
  // סכום קבוע (₪) לא בר-השוואה לאחוזים - ממוין אחרי כל האחוזים, לפי הסכום
  discount: (b) => (b.discountFixed != null ? 1000 + Number(b.discountFixed) : Number(b.discountPercent || 0)),
  city:     (b) => b.city || '',
  codes:    (b) => Number(b.transactionsRedeemedCount || 0),
  order:    (b) => Number(b.sortOrder || 0),
  status:   (b) => (b.active ? 0 : 1),
  store:    (b) => ({ waiting: 0, connected: 1, physical: 2 }[storeState(b)]),
  owner:    (b) => ({ confirmed: 0, in_progress: 1, pending: 2, rejected: 3 }[effectiveLeadStatus(b)]),
};

// טאב "בתי עסק" - הרשימה הפעילה (מה-Businesses, לא הצעות ממתינות - אלה
// בטאב הצעות בתי עסק). "השתקה"/"שחזור" = השבתה/הפעלה (active,
// BOToggleBusinessActive) לפי המוסכמה בפרויקט - לא DELETE אמיתי (ראו
// adminBusinesses.tsx באפליקציה, אותה לוגיקה בדיוק בצד השרת). "מחיקה
// לצמיתות" (deleteBusiness/spBODeleteBusiness) היא כן DELETE אמיתי -
// פעולה נפרדת ומכוונת, חסומה בשרת אם כבר הונפקו לבית העסק עסקאות
const BusinessesTab = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [search, setSearch] = useState('');
  const [defaultFilters, setDefaultFilters] = useState(loadDefaultFilters);
  const [statusFilter, setStatusFilter] = useState(defaultFilters.status);
  const [ownerConfirmFilter, setOwnerConfirmFilter] = useState(defaultFilters.owner);
  const [storeFilter, setStoreFilter] = useState(defaultFilters.store);
  const [sortKey, setSortKey] = useState(defaultFilters.sortKey);
  const [sortDir, setSortDir] = useState(defaultFilters.sortDir);
  const [defaultSavedFlash, setDefaultSavedFlash] = useState(false);
  const [hoverId, setHoverId] = useState(null);
  // תפריט "⋯" הפתוח (id של בית עסק) - נסגר בלחיצה בחוץ או ב-Escape
  const [menuId, setMenuId] = useState(null);
  useEffect(() => {
    if (menuId == null) return;
    const close = () => setMenuId(null);
    const onKey = (e) => e.key === 'Escape' && close();
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuId]);
  // תאים עם כפתורים - לחיצה בתוכם לא פותחת את עמוד הפרטים
  const stop = (e) => e.stopPropagation();
  const [toggleBusyId, setToggleBusyId] = useState(null);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deletingBusiness, setDeletingBusiness] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingPending, setImportingPending] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [followUpBusiness, setFollowUpBusiness] = useState(null);

  // קישור אישור התנאים לבעל עסק חדש - מוגש ישירות מהשרת (לא מהאפליקציה/
  // הבק אופיס, ראו server/src/routes/ownerConfirmRouter.ts), אותו דומיין
  // בדיוק כמו כתובת ה-API (utils.BaseUrl) - אין צורך בדומיין נפרד
  const copyConfirmLink = async (business) => {
    const link = `${utils.BaseUrl}/confirm-business?token=${business.confirmToken}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      window.prompt('העתיקו את הקישור:', link);
      return;
    }
    setCopiedId(business.id);
    setTimeout(() => setCopiedId((id) => (id === business.id ? null : id)), 2500);
  };

  // שליחת הקישור בוואטסאפ - אם יש טלפון לבית העסק פותח שיחה ישירות איתו
  // (0501234567 -> 972501234567), אחרת פותח וואטסאפ לבחירת איש קשר
  const shareConfirmLinkWhatsApp = (business) => {
    const link = `${utils.BaseUrl}/confirm-business?token=${business.confirmToken}`;
    const text = `שלום, זה הקישור לאישור ההצטרפות של "${business.name}" למועדון איגוד: ${link}`;
    const digits = String(business.phone || '').replace(/[^0-9]/g, '');
    const intl = digits.startsWith('972') ? digits : digits.startsWith('0') ? `972${digits.slice(1)}` : digits;
    window.open(`https://wa.me/${intl}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };

  useEffect(() => {
    loadBusinesses();
  }, []);

  const loadBusinesses = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await BOAPI.fetchBusinesses();
      if (resp?.[0]?.[0]?.code === 0) setBusinesses(resp[1] || []);
      else setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת בתי העסק');
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const toggleActive = async (business) => {
    setToggleBusyId(business.id);
    setError('');
    try {
      const resp = await BOAPI.toggleBusinessActive(business.id);
      if (resp?.[0]?.[0]?.code === 0) {
        const active = !!resp[1]?.[0]?.active;
        setBusinesses((prev) => prev.map((b) => (b.id === business.id ? { ...b, active } : b)));
      } else {
        setError(resp?.[0]?.[0]?.msg || 'העדכון נכשל');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setToggleBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deletingBusiness) return;
    setDeleteBusy(true);
    setError('');
    try {
      const resp = await BOAPI.deleteBusiness(deletingBusiness.id);
      if (resp?.[0]?.[0]?.code === 0) {
        setBusinesses((prev) => prev.filter((b) => b.id !== deletingBusiness.id));
        showSuccess(`"${deletingBusiness.name}" נמחק לצמיתות`);
        setDeletingBusiness(null);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'המחיקה נכשלה');
        setDeletingBusiness(null);
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setDeleteBusy(false);
    }
  };

  // סינון - שלוש קבוצות + חיפוש חופשי; כל קבוצה מוגבלת לתוצאות של האחרות
  const matchStatus = (b) => statusFilter === 'all' || (statusFilter === 'active' ? b.active : !b.active);
  const matchOwner = (b) => ownerConfirmFilter === 'all' || effectiveLeadStatus(b) === ownerConfirmFilter;
  const matchStore = (b) => storeFilter === 'all' || storeState(b) === storeFilter;
  const q = search.trim().toLowerCase();
  const matchSearch = (b) => !q || [b.name, b.city, b.address, b.ownerName, b.phone].some((v) => (v || '').toLowerCase().includes(q));

  const filteredBusinesses = useMemo(() => {
    const getter = SORTERS[sortKey] || SORTERS.order;
    const dir = sortDir === 'asc' ? 1 : -1;
    return businesses
      .filter((b) => matchStatus(b) && matchOwner(b) && matchStore(b) && matchSearch(b))
      .sort((a, b) => {
        const va = getter(a), vb = getter(b);
        const cmp = typeof va === 'string' ? va.localeCompare(vb, 'he') : va - vb;
        return cmp * dir || (a.name || '').localeCompare(b.name || '', 'he');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businesses, statusFilter, ownerConfirmFilter, storeFilter, q, sortKey, sortDir]);

  // ספירה בכל קבוצה - לפי שאר הסינונים הפעילים (מה שיוצג אם בוחרים באפשרות)
  const countByStatus = (key) => businesses.filter((b) => matchOwner(b) && matchStore(b) && matchSearch(b) && (key === 'all' || (key === 'active' ? b.active : !b.active))).length;
  const countByOwnerConfirm = (key) => businesses.filter((b) => matchStatus(b) && matchStore(b) && matchSearch(b) && (key === 'all' || effectiveLeadStatus(b) === key)).length;
  const countByStore = (key) => businesses.filter((b) => matchStatus(b) && matchOwner(b) && matchSearch(b) && (key === 'all' || storeState(b) === key)).length;

  const currentFilters = { status: statusFilter, owner: ownerConfirmFilter, store: storeFilter, sortKey, sortDir };
  const sameFilters = (a, b) => Object.keys(ALL_FILTERS).every((k) => a[k] === b[k]);
  const differsFromDefault = !sameFilters(currentFilters, defaultFilters) || !!q;
  const hasCustomDefault = !sameFilters(defaultFilters, ALL_FILTERS);

  const applyFilters = (f) => {
    setStatusFilter(f.status);
    setOwnerConfirmFilter(f.owner);
    setStoreFilter(f.store);
    setSortKey(f.sortKey);
    setSortDir(f.sortDir);
    setSearch('');
  };
  const resetToDefault = () => applyFilters(defaultFilters);
  const saveAsDefault = () => {
    try {
      window.localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(currentFilters));
    } catch { /* בלי אחסון - ברירת המחדל תחזיק רק עד רענון */ }
    setDefaultFilters(currentFilters);
    setDefaultSavedFlash(true);
    setTimeout(() => setDefaultSavedFlash(false), 2000);
  };
  const clearDefault = () => {
    try { window.localStorage.removeItem(FILTERS_STORAGE_KEY); } catch { /* ignore */ }
    setDefaultFilters(ALL_FILTERS);
    applyFilters(ALL_FILTERS);
  };

  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  // כותרת ממיינת - כפתור אמיתי (נגיש במקלדת) + aria-sort לקוראי מסך
  const SortTh = ({ k, children }) => (
    <th style={s.th} scope="col" aria-sort={sortKey === k ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button style={s.sortBtn} onClick={() => onSort(k)} title="לחצו למיון">
        {children}
        <span style={{ ...s.sortArrow, opacity: sortKey === k ? 1 : 0.25 }} aria-hidden="true">{sortKey === k && sortDir === 'desc' ? '▼' : '▲'}</span>
      </button>
    </th>
  );

  // שמות מוצרים ייחודיים מכל בתי העסק הקיימים - מוצע כ-autocomplete בטופס
  // עריכה/יצירה (datalist), כדי שאדמינים ישתמשו באותו שם ("חולצה") ולא
  // ייצרו כפילויות דומות ("חולצות"/"חולצה כחולה") - ראו BusinessEditModal.jsx
  const existingProductNames = useMemo(() => {
    const set = new Set();
    businesses.forEach((b) => (b.products || []).forEach((p) => set.add(p)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'he'));
  }, [businesses]);

  // מייצא את הרשימה המסוננת כרגע על המסך (חיפוש/טאב סטטוס) - לא בהכרח את כל
  // בתי העסק. עמלת הסליקה (commissionPercent) מותרת כאן כי זה קובץ בק אופיס
  // פנימי בלבד, בניגוד לאפליקציה שלעולם לא מציגה אותה ללקוח (ראו הערה ב-Tables.sql)
  const exportToExcel = () => {
    const data = filteredBusinesses.map((b) => ({
      'שם': b.name,
      'קטגוריות': (b.categoryLabels?.length ? b.categoryLabels : [b.categoryLabel || b.category]).join(', '),
      'הטבה': discountLabel(b),
      'עמלת סליקה': commissionLabel(b),
      'עיר': b.city || '',
      'כתובת': b.address || '',
      'טלפון': b.phone || '',
      'בעלים': b.ownerName || '',
      'מוצרים': (b.products || []).join(', '),
      'קודים שהונפקו': b.transactionsRequestedCount ?? 0,
      'קודים שמומשו': b.transactionsRedeemedCount ?? 0,
      'סדר תצוגה': b.sortOrder ?? 0,
      'סטטוס': b.active ? 'פעיל' : 'מוסתר',
      'חנות מקוונת': STORE_INFO[storeState(b)].label,
      'אישור בעלים': LEAD_STATUS_INFO[effectiveLeadStatus(b)].label,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = Object.keys(data[0] || {}).map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'בתי עסק');
    XLSX.writeFile(wb, `בתי-עסק-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>בתי עסק</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={s.totalBadge}>{businesses.length} בתי עסק</span>
          <button style={s.exportBtn} onClick={exportToExcel} disabled={filteredBusinesses.length === 0}>
            ⬇ ייצוא לאקסל
          </button>
          <button style={s.importBtn} onClick={() => setImporting(true)}>📥 ייבוא מאקסל</button>
          <button style={s.importBtn} onClick={() => setImportingPending(true)}>📥 ייבוא ממתינים + לינק</button>
          <button style={s.addBtn} onClick={() => setCreating(true)}>+ בית עסק חדש</button>
        </div>
      </div>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError('')}>✕</button>
        </div>
      )}
      {successMsg && <div style={s.successBanner}>✅ {successMsg}</div>}

      {/* סרגל סינון - חיפוש + שלוש רשימות נפתחות (עם ספירה לכל אפשרות), ושורת
          סיכום עם שמירת ברירת מחדל / איפוס */}
      <div style={s.filterPanel}>
        <div style={s.filterBar}>
          <input
            type="text"
            placeholder="🔍 חיפוש לפי שם, עיר, כתובת, בעלים או טלפון..."
            style={s.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {[
            { label: 'סטטוס', tabs: STATUS_TABS, value: statusFilter, set: setStatusFilter, count: countByStatus },
            { label: 'אישור בעלים', tabs: OWNER_CONFIRM_TABS, value: ownerConfirmFilter, set: setOwnerConfirmFilter, count: countByOwnerConfirm },
            { label: 'חנות מקוונת', tabs: STORE_TABS, value: storeFilter, set: setStoreFilter, count: countByStore },
          ].map((group) => (
            <label key={group.label} style={s.selectWrap}>
              <span style={s.selectLabel}>{group.label}</span>
              <select
                style={{ ...s.filterSelect, ...(group.value !== 'all' ? s.filterSelectActive : {}) }}
                value={group.value}
                onChange={(e) => group.set(e.target.value)}
              >
                {group.tabs.map((t) => (
                  <option key={t.key} value={t.key}>{t.label} ({group.count(t.key)})</option>
                ))}
              </select>
            </label>
          ))}
        </div>

        <div style={s.filterFooter}>
          <span style={s.resultCount}>מציג {filteredBusinesses.length} מתוך {businesses.length}</span>
          <span style={{ flex: 1 }} />
          {differsFromDefault && <button style={s.linkBtn} onClick={resetToDefault}>↺ {hasCustomDefault ? 'חזרה לברירת המחדל' : 'ניקוי סינון'}</button>}
          {!sameFilters(currentFilters, defaultFilters) && <button style={s.linkBtn} onClick={saveAsDefault}>⭐ שמירה כברירת מחדל</button>}
          {defaultSavedFlash && <span style={s.savedNote}>✓ נשמר - ייטען אוטומטית בכניסה הבאה</span>}
          {hasCustomDefault && !defaultSavedFlash && <button style={{ ...s.linkBtn, color: '#8792a2' }} onClick={clearDefault}>ביטול ברירת המחדל</button>}
        </div>
      </div>

      {loading ? (
        <div style={s.center}>טוען בתי עסק...</div>
      ) : filteredBusinesses.length === 0 ? (
        <div style={s.emptyState}>
          <div style={{ fontSize: 48 }}>🏪</div>
          <p style={{ color: '#697386', fontWeight: 600 }}>לא נמצאו בתי עסק</p>
        </div>
      ) : (
        <div style={s.tableWrapper}>
          {/* לחיצה על שורה (או Enter כשהיא בפוקוס) פותחת את עמוד הפרטים - עובדים
              ועסקאות. כפתורים בתוך השורה עוצרים את הלחיצה (stop) כדי לא לנווט */}
          <table style={s.table}>
            <caption style={s.srOnly}>רשימת בתי העסק - לחיצה על שורה פותחת את פרטי בית העסק</caption>
            <thead>
              <tr style={s.headerRow2}>
                <SortTh k="name">בית עסק</SortTh>
                <SortTh k="discount">הנחה</SortTh>
                <th style={s.th} scope="col">בעלים</th>
                <SortTh k="codes"><span title="הונפקו / מומשו">קודים</span></SortTh>
                <SortTh k="status">סטטוס</SortTh>
                <SortTh k="store">חנות מקוונת</SortTh>
                <SortTh k="owner">אישור בעלים</SortTh>
                <th style={{ ...s.th, width: 1 }} scope="col"><span style={s.srOnly}>פעולות</span></th>
              </tr>
            </thead>
            <tbody>
              {filteredBusinesses.map((b) => {
                const openDetails = () => navigate(`/businesses/${b.id}`);
                const categories = (b.categoryLabels?.length ? b.categoryLabels : [b.categoryLabel || b.category]).filter(Boolean).join(', ');
                return (
                  <tr
                    key={b.id}
                    style={{ ...s.row, ...(hoverId === b.id ? s.rowHover : {}), ...(b.active ? {} : s.rowInactive) }}
                    onClick={openDetails}
                    onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) openDetails(); }}
                    onMouseEnter={() => setHoverId(b.id)}
                    onMouseLeave={() => setHoverId(null)}
                    tabIndex={0}
                    aria-label={`${b.name} - פתיחת פרטים, עובדים ועסקאות`}
                  >
                    <td style={s.td}>
                      <div style={s.nameCell}>
                        {b.logoUrl ? (
                          <img src={fullUrl(b.logoUrl)} alt="" style={s.logo} />
                        ) : (
                          <div style={s.logoPlaceholder} aria-hidden="true">{(b.name || '?')[0]}</div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={s.nameText}>{b.name}</div>
                          <div style={s.subText}>{[categories, b.city].filter(Boolean).join(' · ') || '-'}</div>
                        </div>
                      </div>
                    </td>
                    <td style={s.td}><span style={s.discountBadge}>{discountLabel(b)}</span></td>
                    <td style={s.td}>{b.ownerName || <span style={s.muted}>ללא בעלים</span>}</td>
                    <td style={{ ...s.td, whiteSpace: 'nowrap' }} title="הונפקו / מומשו">
                      {b.transactionsRequestedCount ?? 0} / {b.transactionsRedeemedCount ?? 0}
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.statusBadge, ...(b.active ? s.statusActive : s.statusInactive) }}>
                        {b.active ? 'פעיל' : 'מוסתר'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <span
                        title={storeState(b) === 'waiting' ? 'חנות מקוונת - החיבור ל-WooCommerce/Shopify עוד לא הושלם' : ''}
                        style={{ ...s.statusBadge, ...s[STORE_INFO[storeState(b)].style] }}
                      >
                        {STORE_INFO[storeState(b)].label}
                      </span>
                      {storeState(b) !== 'physical' && !!b.onlinePlatform && (
                        <div style={s.platformText}>{b.onlinePlatform === 'shopify' ? 'Shopify' : 'WooCommerce'}</div>
                      )}
                    </td>
                    <td style={s.td} onClick={stop}>
                      {b.ownerConfirmedAt ? (
                        <span style={{ ...s.statusBadge, ...s[LEAD_STATUS_INFO.confirmed.style] }}>{LEAD_STATUS_INFO.confirmed.label}</span>
                      ) : (
                        <div style={s.leadCell}>
                          <span style={{ ...s.statusBadge, ...s[LEAD_STATUS_INFO[b.leadStatus || 'pending'].style] }}>
                            {LEAD_STATUS_INFO[b.leadStatus || 'pending'].label}
                          </span>
                          <div style={s.iconBtns}>
                            <button style={s.iconBtn} onClick={() => copyConfirmLink(b)} title="העתקת קישור האישור" aria-label={`העתקת קישור האישור של ${b.name}`}>
                              {copiedId === b.id ? '✓' : '🔗'}
                            </button>
                            <button style={s.iconBtn} onClick={() => shareConfirmLinkWhatsApp(b)} title="שליחת הקישור בוואטסאפ" aria-label={`שליחת קישור האישור של ${b.name} בוואטסאפ`}>
                              📲
                            </button>
                            <button
                              style={{ ...s.iconBtn, ...(b.followUpAt ? (isFollowUpDue(b.followUpAt) ? s.followUpBadgeDue : s.followUpBadge) : {}) }}
                              onClick={() => setFollowUpBusiness(b)}
                              title={b.followUpAt ? `מעקב: ${fmtFollowUpDate(b.followUpAt)}${b.followUpNote ? ` - ${b.followUpNote}` : ''}` : 'קביעת מעקב'}
                              aria-label={`מעקב עבור ${b.name}`}
                            >
                              📅{b.followUpAt ? ` ${fmtFollowUpDate(b.followUpAt)}` : ''}
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                    <td style={{ ...s.td, whiteSpace: 'nowrap' }} onClick={stop}>
                      <div style={s.actionsCell}>
                        <button style={s.editBtn} onClick={() => setEditingBusiness(b)}>✏️ עריכה</button>
                        <div style={{ position: 'relative' }}>
                          <button
                            style={s.moreBtn}
                            onClick={() => setMenuId((id) => (id === b.id ? null : b.id))}
                            aria-haspopup="menu"
                            aria-expanded={menuId === b.id}
                            aria-label={`פעולות נוספות עבור ${b.name}`}
                          >
                            ⋯
                          </button>
                          {menuId === b.id && (
                            <div style={s.menu} role="menu">
                              <button
                                role="menuitem"
                                style={s.menuItem}
                                onClick={() => { setMenuId(null); openDetails(); }}
                              >
                                פרטים, עובדים ועסקאות
                              </button>
                              <button
                                role="menuitem"
                                style={s.menuItem}
                                onClick={() => { setMenuId(null); toggleActive(b); }}
                                disabled={toggleBusyId === b.id}
                              >
                                {b.active ? 'השתקה (הסתרה מהאפליקציה)' : 'שחזור (הצגה באפליקציה)'}
                              </button>
                              <div style={s.menuDivider} />
                              <button
                                role="menuitem"
                                style={{ ...s.menuItem, color: '#d93838' }}
                                onClick={() => { setMenuId(null); setDeletingBusiness(b); }}
                              >
                                🗑️ מחיקה לצמיתות
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <BusinessEditModal
        business={editingBusiness}
        existingProductNames={existingProductNames}
        onClose={() => setEditingBusiness(null)}
        onSaved={(updated) => {
          if (!updated) return;
          setBusinesses((prev) => prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b)));
          setEditingBusiness(null);
          showSuccess(`"${updated.name}" נשמר בהצלחה`);
        }}
      />

      {creating && (
        <BusinessEditModal
          business={{}}
          existingProductNames={existingProductNames}
          onClose={() => setCreating(false)}
          onSaved={(created) => {
            if (!created) return;
            setBusinesses((prev) => [created, ...prev]);
            setCreating(false);
            showSuccess(`"${created.name}" נוסף בהצלחה`);
          }}
        />
      )}

      <ImportBusinessesModal
        visible={importing}
        onClose={() => setImporting(false)}
        onImported={() => showSuccess('הייבוא הושלם - בדקו את התוצאות בטאב "הצעות בתי עסק" לאישור')}
      />

      <ImportPendingBusinessesModal
        visible={importingPending}
        onClose={() => setImportingPending(false)}
        onImported={() => { showSuccess('הייבוא הושלם - בתי העסק נוספו כממתינים, אפשר לשלוח לכל אחד את הקישור שלו'); loadBusinesses(); }}
      />

      <FollowUpModal
        business={followUpBusiness}
        onClose={() => setFollowUpBusiness(null)}
        onSaved={(updated) => {
          setBusinesses((prev) => prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b)));
          setFollowUpBusiness(null);
        }}
      />

      {deletingBusiness && (
        <div style={s.overlay} onClick={() => !deleteBusy && setDeletingBusiness(null)}>
          <div style={s.deleteModalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={s.deleteModalTitle}>מחיקת בית עסק לצמיתות</h3>
            <p style={s.deleteModalText}>
              למחוק את <strong>"{deletingBusiness.name}"</strong> לצמיתות מהמערכת?
              הפעולה לא ניתנת לביטול. אם כבר הונפקו לבית העסק עסקאות, המחיקה תיחסם -
              אפשר להשתיק אותו במקום.
            </p>
            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setDeletingBusiness(null)} disabled={deleteBusy}>ביטול</button>
              <button style={s.confirmDeleteBtn} onClick={confirmDelete} disabled={deleteBusy}>
                {deleteBusy ? 'מוחק...' : 'מחיקה לצמיתות'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  container:    { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  title:        { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  totalBadge:   { background: '#e8eaf6', color: '#1f6feb', borderRadius: 12, padding: '4px 12px', fontSize: 13, fontWeight: 700 },
  addBtn:       { padding: '9px 16px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  exportBtn:    { padding: '9px 16px', background: '#fff', color: '#1f6feb', border: '1.5px solid #c7d7fb', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  importBtn:    { padding: '9px 16px', background: '#fff', color: '#1f6feb', border: '1.5px solid #c7d7fb', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  center:       { padding: 40, textAlign: 'center', color: '#999' },
  errorBanner:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:     { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },
  successBanner:{ background: '#e8f5e9', color: '#2e7d32', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 },
  emptyState:   { textAlign: 'center', padding: '60px 0' },
  tabsRow:      { display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' },
  filterLabel:  { fontSize: 12.5, fontWeight: 700, color: '#8792a2', minWidth: 88 },
  tabBtn:       { padding: '8px 16px', borderRadius: 999, border: '1.5px solid #e3e8ee', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#697386' },
  tabBtnActive: { background: '#1f6feb', borderColor: '#1f6feb', color: '#fff' },
  tabCount:     { opacity: 0.7, fontWeight: 600 },
  searchInput:  { flex: 1, minWidth: 240, alignSelf: 'flex-end', padding: '9px 14px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 13, boxSizing: 'border-box', height: 38 },
  filterPanel:  { background: '#fff', border: '1px solid #e3e8ee', borderRadius: 14, padding: '14px 16px', marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 10 },
  filterBar:    { display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' },
  selectWrap:   { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 150 },
  selectLabel:  { fontSize: 11.5, fontWeight: 700, color: '#8792a2' },
  filterSelect: { height: 38, padding: '0 10px', borderRadius: 10, border: '1.5px solid #e3e8ee', background: '#fff', fontSize: 13, fontWeight: 600, color: '#1a1f36', cursor: 'pointer', fontFamily: 'inherit' },
  filterSelectActive: { borderColor: '#1f6feb', background: '#eef4ff', color: '#1f6feb' },
  linkBtn:      { background: 'none', border: 'none', padding: '4px 6px', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: '#1f6feb', fontFamily: 'inherit' },
  savedNote:    { fontSize: 12.5, fontWeight: 600, color: '#2e7d32' },
  filterGroup:  { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  segment:      { display: 'flex', flexWrap: 'wrap', border: '1.5px solid #e3e8ee', borderRadius: 10, overflow: 'hidden' },
  segmentBtn:   { padding: '7px 14px', background: '#fff', color: '#697386', border: 'none', borderInlineStart: '1px solid #eef0f3', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit' },
  segmentBtnActive: { background: '#1f6feb', color: '#fff' },
  filterFooter: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderTop: '1px solid #f0f2f5', paddingTop: 10 },
  resultCount:  { fontSize: 12.5, color: '#697386', fontWeight: 600 },
  clearBtn:     { padding: '7px 12px', background: '#fff', color: '#d93838', border: '1px solid #fbc4c4', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  sortBtn:      { background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 700, color: 'inherit', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' },
  srOnly:       { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 },
  rowHover:     { background: '#f7f9fc', cursor: 'pointer' },
  rowInactive:  { opacity: 0.65 },
  nameCell:     { display: 'flex', alignItems: 'center', gap: 12, minWidth: 220 },
  nameText:     { fontWeight: 700, color: '#1a1f36', fontSize: 14 },
  subText:      { fontSize: 12, color: '#8792a2', marginTop: 2, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  muted:        { color: '#a3acb9' },
  leadCell:     { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  iconBtns:     { display: 'flex', gap: 4 },
  iconBtn:      { minWidth: 32, height: 30, padding: '0 8px', background: '#fff', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
  actionsCell:  { display: 'flex', gap: 6, alignItems: 'center' },
  moreBtn:      { width: 34, height: 32, background: '#fff', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 8, cursor: 'pointer', fontSize: 18, fontWeight: 700, lineHeight: 1 },
  menu:         { position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 20, minWidth: 220, background: '#fff', border: '1px solid #e3e8ee', borderRadius: 10, boxShadow: '0 10px 30px rgba(15,23,42,0.15)', padding: 6, display: 'flex', flexDirection: 'column' },
  menuItem:     { background: 'none', border: 'none', textAlign: 'right', padding: '9px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1a1f36', fontFamily: 'inherit' },
  menuDivider:  { height: 1, background: '#f0f2f5', margin: '4px 0' },
  sortArrow:    { fontSize: 9, marginInlineStart: 4, color: '#1f6feb' },
  statusNeutral:{ background: '#f2f4f7', color: '#697386' },
  platformText: { fontSize: 11, color: '#8792a2', marginTop: 4 },

  tableWrapper: { overflowX: 'auto', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  table:        { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow2:   { backgroundColor: '#f5f7fa', borderBottom: '2px solid #e3e8ee' },
  th:           { padding: '14px 16px', textAlign: 'right', fontWeight: 700, color: '#4f566b', fontSize: 13, borderBottom: '2px solid #e3e8ee' },
  row:          { borderBottom: '1px solid #f0f2f5' },
  td:           { padding: '10px 16px', color: '#4f566b', fontSize: 13, textAlign: 'right' },
  logo:         { width: 38, height: 38, borderRadius: 8, objectFit: 'cover' },
  logoPlaceholder: { width: 38, height: 38, borderRadius: 8, background: '#EEF2FF', color: '#1f6feb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15 },
  nameLink:     { background: 'none', border: 'none', cursor: 'pointer', color: '#1f6feb', fontWeight: 700, fontSize: 13, padding: 0, textAlign: 'right' },
  discountBadge:{ display: 'inline-block', background: '#ECFDF5', color: '#16A34A', borderRadius: 10, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  statusBadge:  { display: 'inline-block', borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
  statusActive: { background: '#e8f5e9', color: '#2e7d32' },
  statusInactive:{ background: '#fff1f1', color: '#d93838' },
  statusPending: { background: '#fff8e1', color: '#b58105' },
  statusInProgress: { background: '#EEF2FF', color: '#1f6feb' },
  copyLinkBtn:  { padding: '6px 10px', background: '#fff', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 8, cursor: 'pointer', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' },
  followUpBadge:   { background: '#EEF2FF', color: '#1f6feb', border: '1.5px solid #c7d7fb' },
  followUpBadgeDue:{ background: '#fff1f1', color: '#c62828', border: '1.5px solid #fbc4c4' },
  rowActions:   { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
  editBtn:      { padding: '6px 12px', background: '#EEF2FF', color: '#1f6feb', border: '1px solid #c7d7fb', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  activateBtn:  { padding: '6px 12px', background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  deactivateBtn:{ padding: '6px 12px', background: '#fff1f1', color: '#d93838', border: '1px solid #fbc4c4', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  deleteBtn:    { padding: '6px 12px', background: '#fff', color: '#c62828', border: '1px solid #c62828', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },

  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100, padding: 16, direction: 'rtl',
  },
  deleteModalCard: {
    width: '100%', maxWidth: 420, background: '#fff', borderRadius: 20,
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)', padding: 24,
  },
  deleteModalTitle: { margin: '0 0 14px', fontSize: 17, fontWeight: 800, color: '#c62828' },
  deleteModalText: { fontSize: 13.5, color: '#4f566b', lineHeight: 1.6, marginBottom: 18 },
  modalActions: { display: 'flex', gap: 10 },
  cancelBtn: { flex: 1, padding: '11px 16px', background: '#fff', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  confirmDeleteBtn: { flex: 1, padding: '11px 16px', background: '#c62828', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
};

export default BusinessesTab;
